import React from "react";
import { Link, useParams } from "react-router-dom";
import { Skeleton } from "@/components/ui/skeleton";
import { SITE, siteApi, useLive, usePageMeta, fmtDate } from "@/lib/site";
import { PageHeader, Section, Prose } from "@/components/site/SiteBits";
import { NotFound } from "@/components/SiteLinks";

// ---------------------------------------------------------------- blog (live from the shared backend, seed posts as fallback)
const fromApi = (p) => ({
  slug: p.slug,
  title: p.title,
  dek: p.excerpt || p.seo_description || "",
  date: (p.published_at || p.created_at || "").slice(0, 10),
  body: p.body ? p.body.split(/\n{2,}/).map((x) => x.trim()).filter(Boolean) : null,
});

export function BlogPage() {
  usePageMeta("Blog", "Product updates, guides and stories from the Fable + community.");
  const live = useLive(() => siteApi.blog());
  const posts = live === undefined ? undefined : live?.posts?.length ? live.posts.map(fromApi) : SITE.posts;
  return (
    <div>
      <PageHeader kicker="Blog" title="News & guides" lede="Product updates, guides and stories from the Fable + community." />
      <Section>
        <div className="grid gap-4 md:grid-cols-2">
          {posts === undefined
            ? Array.from({ length: 2 }).map((_, i) => <Skeleton key={i} className="h-44 rounded-2xl" />)
            : posts.map((p) => (
                <Link key={p.slug} to={`/blog/${p.slug}`} className="block rounded-2xl border border-border bg-card p-6 card-hover" data-testid={`post-${p.slug}`}>
                  {p.date && <p className="text-xs text-muted-foreground">{fmtDate(p.date)}</p>}
                  <h3 className="mt-2 text-xl font-bold">{p.title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground">{p.dek}</p>
                  <span className="mt-4 inline-block text-sm font-semibold text-primary">Read →</span>
                </Link>
              ))}
        </div>
      </Section>
    </div>
  );
}

export function BlogPostPage() {
  const { slug } = useParams();
  const live = useLive(() => siteApi.post(slug), [slug]);
  const seed = SITE.posts.find((p) => p.slug === slug);
  const post = live ? { ...(seed || {}), ...Object.fromEntries(Object.entries(fromApi(live)).filter(([, v]) => v)) } : live === null ? seed : undefined;
  usePageMeta(post?.title || "Blog", post?.dek);
  if (live === undefined && !seed) return <Section><Skeleton className="h-64 rounded-2xl" /></Section>;
  if (!post && live === null) return <NotFound />;
  const p = post || seed;
  return (
    <div>
      <PageHeader kicker={<><Link to="/blog" className="hover:underline">Blog</Link>{p.date ? ` · ${fmtDate(p.date)}` : ""}</>} title={p.title} lede={p.dek} />
      <Section>
        <Prose>{(p.body || []).map((x, i) => <p key={i}>{x}</p>)}</Prose>
      </Section>
    </div>
  );
}

// ---------------------------------------------------------------- legal
export function LegalDocPage({ doc }) {
  const d = SITE.legal[doc];
  usePageMeta(d.title, d.intro.slice(0, 155));
  return (
    <div>
      <PageHeader kicker="Legal" title={d.title} lede={d.intro}>
        {d.updated && <p className="text-sm text-muted-foreground">Last updated {d.updated}</p>}
      </PageHeader>
      <Section>
        <div className="grid items-start gap-10 lg:grid-cols-[240px_1fr]">
          <nav className="sticky top-24 hidden max-h-[70vh] overflow-auto text-sm lg:block" aria-label="On this page">
            <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">On this page</p>
            <ul className="space-y-2">
              {d.sections.map((s, i) => (
                <li key={i}><a href={`#s${i}`} className="text-foreground/70 hover:text-primary">{s.h}</a></li>
              ))}
            </ul>
          </nav>
          <Prose>
            {d.sections.map((s, i) => (
              <React.Fragment key={i}>
                <h2 id={`s${i}`} className="scroll-mt-24" style={i === 0 ? { marginTop: 0 } : undefined}>{s.h}</h2>
                {s.body.split(/\n{2,}/).map((x, j) => <p key={j}>{x}</p>)}
              </React.Fragment>
            ))}
          </Prose>
        </div>
      </Section>
    </div>
  );
}

export function AffiliatePage() {
  usePageMeta("Affiliate disclosure", "How Fable + earns from travel links.");
  return (
    <div>
      <PageHeader kicker="Legal" title="Affiliate disclosure" lede="Fable + is free to use. Some of our travel links help pay for that.">
        <p className="text-sm text-muted-foreground">Last updated 29 October 2026</p>
      </PageHeader>
      <Section>
        <Prose>
          <h2 style={{ marginTop: 0 }}>What an affiliate link is</h2>
          <p>Some links on this website and in the Fable + app, mainly flight, eSIM and activity links, are affiliate links. If you click one and make a booking or purchase, the company you buy from may pay Fable + a commission.</p>
          <h2>What it means for you</h2>
          <ul>
            <li>You never pay more. The price is the same as going to the site directly.</li>
            <li>You buy from the partner, not from us. Your booking, payment, changes and refunds are handled by the partner under their terms.</li>
            <li>Commissions never decide what we show first. Flight results are sorted by price.</li>
          </ul>
          <h2>Who we work with</h2>
          <p>We work with travel brands through the Travelpayouts affiliate network, including Aviasales (flights), Airalo (eSIMs) and Klook (tours and activities). We may add partners over time and will update this page when we do.</p>
          <h2>How links are labelled</h2>
          <p>Affiliate links open in a new tab and are marked with an arrow icon and a short note nearby. Partner sites may set cookies on their own website to record that you came from Fable +.</p>
          <h2>Questions</h2>
          <p>Email <a href={`mailto:${SITE.email}`}>{SITE.email}</a>.</p>
        </Prose>
      </Section>
    </div>
  );
}

export function CookiesPage() {
  usePageMeta("Cookies", "How the Fable + website uses cookies and browser storage.");
  return (
    <div>
      <PageHeader kicker="Legal" title="Cookies" lede="We only use what the website needs to work. No advertising cookies and no tracking pixels.">
        <p className="text-sm text-muted-foreground">Last updated 30 October 2026</p>
      </PageHeader>
      <Section>
        <Prose>
          <h2 style={{ marginTop: 0 }}>What we store in your browser</h2>
          <p>When you sign in, we keep a sign-in token in your browser's local storage so you stay signed in between visits. It's strictly necessary for the account features you ask for, and it's removed when you sign out.</p>
          <p>We don't use advertising cookies, analytics cookies or tracking pixels on this website. Our fonts and images are served by us.</p>
          <h2>Payments</h2>
          <p>If you pay by card, you're taken to Stripe's secure checkout, which uses its own cookies for security and fraud prevention under Stripe's policies.</p>
          <h2>Partner sites</h2>
          <p>When you follow an affiliate link to a partner such as Aviasales, Airalo or Klook, that partner and the Travelpayouts network may set cookies on their own sites under their own policies, for example to record that you arrived from Fable +.</p>
          <h2>The Fable + app</h2>
          <p>How the app and website handle your data is set out in our <Link to="/legal/privacy">Privacy Policy</Link>.</p>
        </Prose>
      </Section>
    </div>
  );
}
